<template>
  <div id="InquiryDetail" class="wrap">
    <div class="formWrap">
      <h1 class="title">{{ inquiry.title }}</h1>

      <div class="threadWrap mx-auto">
        <!-- スレッド：上(古い)→下(新しい) -->
        <div
          v-for="(m, i) in inquiry.messages" :key="i"
          :class="['msg', 'p-3', 'mb-3', isCms(m) ? 'msgCms' : 'msgGas']">
          <div class="flex flexMiddle mb-2">
            <span class="bold mr-2">{{ senderName(m) }}</span>
            <span class="msgDate">{{ formatDate(m.created_at) }}</span>
          </div>
          <div class="msgBody">{{ m.body }}</div>
          <div v-if="m.files && m.files.length" class="mt-2">
            <p v-for="(f, fi) in m.files" :key="fi" class="mb-0">
              <b-link @click="downloadFile(f)">{{ f.file_name }}</b-link>
            </p>
          </div>
        </div>

        <!-- 続けて問合せ（インライン入力） -->
        <div v-if="showInput" class="inputArea p-3 mt-3">
          <div class="bold mb-2"><span class="mr-2 badge badge-required">必須</span>続けて問合せ</div>
          <b-form-textarea v-model="body" :rows="5" maxlength="10000" placeholder="入力してください。" class="mb-2"/>
          <p v-for="(f, i) in fileList" :key="i" class="mb-1">
            {{ f.name }}
            <img src="@/assets/svg/close_small.svg" class="tdDeleteBtn" @click="removeFile(i)">
          </p>
          <div
            @dragover.prevent="toggleDrag(true)"
            @dragleave.prevent="toggleDrag(false)"
            @drop.prevent="onDrop($event)"
            :class="[isDragging ? 'is-dragging' : '', 'drop', 'flex', 'flexMiddle', 'flexCenter']">
            <span>
              <span class="dragText fw-6">ここにファイルをドラッグ＆ドロップ または</span>
              <label class="fileText fw-6 selectFile">［ ファイルを選択 ］
                <input type="file" style="display:none;" @change="addFile">
              </label>
            </span>
          </div>
          <div class="mt-3 flex flexCenter">
            <b-button variant="primary" class="btn btn-primary bold" :disabled="sending || !canPost" @click="send">送信</b-button>
          </div>
        </div>

        <p v-if="!canPost" class="periodNote mt-3">現在、問合せができる期間ではありません。閲覧のみ可能です。</p>
      </div>
    </div>

    <div class="btnWrap">
      <b-button class="btn back bold mr-3" @click="goList">一覧に戻る</b-button>
      <b-button
        v-if="!showInput"
        variant="primary"
        class="btn btn-primary bold"
        :disabled="!canPost"
        @click="showInput = true">続けて問合せ</b-button>
    </div>
  </div>
</template>

<script>
import api from '@/modules/api';
import moment from 'moment';
import download from '@/modules/download';
import inquiryContext from '@/mixin/inquiryContext';

export default {
  name: 'gasInquiryDetail',
  mixins: [inquiryContext],
  data() {
    return {
      showInput: false,
      body: '',
      fileList: [],
      isDragging: false,
      sending: false,
      canPost: true, // 送信可否（申請期間中の申請者／採択後〜報告期間終了までの採択者のみ true）
      inquiry: {
        id: null,
        title: '',
        applicant_name: '',
        messages: [],
      },
    };
  },
  async created() {
    await this.fetchDetail();
    // 送信可否を取得（閲覧は常に可・続けて問合せのみ制御）
    try {
      const res = await api.send(`${this.inquiryApiBase}/sendable`);
      this.canPost = !!(res.data && res.data.canSend);
    } catch (e) {
      this.canPost = false;
    }
  },
  methods: {
    async fetchDetail() {
      const { id } = this.$route.query;
      if (!id) {
        this.$router.push({ name: this.routeNames.list });
        return;
      }
      try {
        const res = await api.send(`${this.inquiryApiBase}/detail`, { inquiryId: id });
        this.inquiry = res.data.inquiry;
      } catch (e) {
        this.$bvModal.msgBoxOk('問合せの取得に失敗しました。');
        this.$router.push({ name: this.routeNames.list });
      }
    },
    isCms(m) {
      return m.sender_type === 2;
    },
    senderName(m) {
      return this.isCms(m) ? '事務局' : this.inquiry.applicant_name;
    },
    formatDate(dt) {
      return dt ? moment(dt).format('YYYY/MM/DD HH:mm') : '';
    },
    async downloadFile(f) {
      try {
        const res = await api.send(`${this.inquiryApiBase}/download`, { fileId: f.id }, true);
        download.blob(res.data, f.file_name);
      } catch (e) {
        this.$bvModal.msgBoxOk('ファイルのダウンロードに失敗しました。');
      }
    },
    addFile(event) {
      if (event.target.files.length === 0) {
        return;
      }
      const input = event.target;
      this.fileList.push(input.files[0]);
      input.value = '';
    },
    toggleDrag(isDragging) {
      this.isDragging = isDragging;
    },
    onDrop(event) {
      this.isDragging = false;
      const { files } = event.dataTransfer;
      if (!files || files.length === 0) {
        return;
      }
      Object.keys(files).forEach((key) => {
        this.fileList.push(files[key]);
      });
    },
    removeFile(index) {
      this.fileList.splice(index, 1);
    },
    goList() {
      this.$router.push({ name: this.routeNames.list });
    },
    async send() {
      if (this.sending || !this.canPost) {
        return;
      }
      if (!this.body) {
        this.$bvModal.msgBoxOk('本文を入力してください。');
        return;
      }
      if (!(await this.confirm('現在の内容で問合せしてよろしいですか？'))) {
        return;
      }
      this.sending = true;
      const params = {
        inquiryId: this.inquiry.id,
        body: this.body,
        fileCnt: this.fileList.length,
      };
      this.fileList.forEach((file, i) => {
        params[`file_${i}`] = file;
      });
      try {
        await api.sendForm(`${this.inquiryApiBase}/reply`, params);
        this.showInput = false;
        this.body = '';
        this.fileList = [];
        await this.fetchDetail();
        this.sending = false;
      } catch (e) {
        this.$bvModal.msgBoxOk('送信に失敗しました。');
        this.sending = false;
      }
    },
  },
};
</script>

<style scoped>
  .title {
    color: #333333;
    font-weight: 300;
    font-size: 24px;
    margin: 52px 100px 0;
    border-bottom: solid 2px #A0A9D0;
    padding-left: 10px;
    padding-bottom: 7px;
  }
  .threadWrap {
    width: 880px;
    margin-top: 40px;
  }
  .msg {
    border: solid 1px #CCC;
    border-radius: 6px;
  }
  .msgGas {
    background-color: #fff;
  }
  .msgCms {
    background-color: #EEF3FB;
  }
  .msgDate {
    color: #888;
    font-size: 13px;
  }
  .msgBody {
    white-space: pre-wrap;
  }
  .inputArea {
    background-color: #fff;
    border: solid 1px #A0A9D0;
    border-radius: 6px;
  }
  .periodNote {
    color: #C0392B;
    font-weight: 600;
    text-align: center;
  }
  .drop {
    height: 100px;
    width: 100%;
    background-color: #F3F3F3;
    border-color: #CCC;
    border-style: dashed;
    border-radius: 10px;
    border-width: 1px;
  }
  .is-dragging.drop {
    background-color: #E6E6E6;
    border-color: #6E6E6E;
  }
  .dragText {
    color: #BBB;
  }
  .fileText {
    color: #0A8EA7;
    cursor: pointer;
  }
  .selectFile:hover {
    text-decoration: underline;
  }
  .btnWrap {
    margin: 45px 0;
    text-align: center;
  }
</style>
